/**
 * The app's keyboard shortcuts, in one place.
 *
 * A catalog rather than a runtime registry: nothing needs to enumerate live shortcuts yet, and
 * the thing that genuinely wants a single source of truth is the pairing of a chord with the
 * label we print for it. Those two drifting apart is how a UI ends up advertising a key that
 * does nothing. If a "keyboard shortcuts" help sheet is ever wanted, this is the file it reads.
 *
 * Add new shortcuts to SHORTCUTS below and drive them with `useKeyboardShortcut`.
 */

export interface Shortcut {
  /** `KeyboardEvent.key`, matched case-insensitively. */
  key: string;
}

export const SHORTCUTS = {
  /**
   * Opens the Filters omnifilter on the Library page and the Libraries list, with the cursor in
   * the input.
   *
   * This deliberately overrides the browser's find-in-page on those pages. The grids are
   * virtualised, so find-in-page can only ever search the handful of rows currently in the DOM —
   * it looks like it works and then silently misses most of the library. The filter searches the
   * actual data. Where that trade doesn't hold (a modal is covering the page), the shortcut
   * stands down and native find behaves normally.
   */
  openFilters: { key: 'f' },
} as const satisfies Record<string, Shortcut>;

/**
 * A single key pressed on its own — no modifier. A different kind from `Shortcut`, and kept in a
 * different catalog on purpose: `formatShortcut` prints every `Shortcut` as an accelerator, so a
 * bare `c` filed in `SHORTCUTS` would be advertised as ⌘C, which is copy.
 *
 * These belong to the surface that owns them rather than to `useKeyboardShortcut` (see its note):
 * a bare letter is also a character someone might be typing, so every listener for one needs
 * `isTextEntryTarget` first.
 */
export interface KeyShortcut {
  /** `KeyboardEvent.key`, matched case-insensitively. */
  key: string;
}

export const KEY_SHORTCUTS = {
  /**
   * In the media modal: start a comment at the current frame, or on the marked range — the
   * Comments panel opens with its composer focused. `C`, as review tools (Frame.io and its
   * peers) have trained people to expect.
   */
  startComment: { key: 'c' },
} as const satisfies Record<string, KeyShortcut>;

/**
 * Whether a keydown is `shortcut` pressed alone.
 *
 * Every modifier is rejected, not ignored — ⌘C and Ctrl+C are copy, and Shift+C is a capital
 * someone meant to type. So is a held key's auto-repeat: one press means one action.
 */
export function matchesKey(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'repeat'>,
  shortcut: KeyShortcut,
): boolean {
  if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) return false;
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
  return !event.repeat;
}

/** A bare key written the way it is printed on the keycap: `C`. The partner of `formatShortcut`. */
export function formatKey(shortcut: KeyShortcut): string {
  return shortcut.key.toUpperCase();
}

/**
 * Whether a key event is aimed at something the reader is typing into, where a bare-key shortcut
 * must stand down. The media player's own keys (space, arrows) and the comment shortcut both ask
 * this, so it lives here once rather than inline at each listener.
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target as Element | null)?.getAttribute?.('contenteditable') === 'true'
  );
}

const APPLE_PLATFORM = /Mac|iPhone|iPad|iPod/;

/**
 * Whether this is a platform whose accelerator modifier is Cmd rather than Ctrl.
 *
 * Includes iPhone/iPad deliberately: an iPad with a Magic Keyboard sends ⌘F, and iPadOS Safari
 * reports a desktop-Mac user agent anyway, so the two cases collapse into one.
 *
 * `navigator.platform` is deprecated but universally implemented and — unlike the UA string — not
 * rewritten by Chrome's reduced-user-agent work, so it is checked first with the UA as fallback.
 * The match is case-sensitive on purpose: "Macintosh" and "Mac OS X" are the real spellings, and
 * a case-insensitive /mac/ would match unrelated substrings in exotic UA strings.
 */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return APPLE_PLATFORM.test(navigator.platform ?? '') || APPLE_PLATFORM.test(navigator.userAgent);
}

/**
 * Whether a keydown is the accelerator chord for `shortcut`.
 *
 * `apple` is injected rather than sniffed so this stays pure and testable without a user agent —
 * see `__tests__/shortcuts.test.ts`.
 *
 * The modifier is platform-SPLIT, never `metaKey || ctrlKey`. On macOS, Ctrl+F is the readline
 * forward-character binding inside every text field, so accepting Ctrl there would break cursor
 * movement while the user types. Off macOS, Cmd is the Windows/Super key, which the OS owns.
 *
 * Alt and Shift are rejected rather than ignored, so we don't swallow a distinct chord (⇧⌘F, ⌥⌘F)
 * that the browser or OS may have its own meaning for.
 */
export function matchesShortcut(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>,
  shortcut: Shortcut,
  apple: boolean,
): boolean {
  if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) return false;
  if (event.altKey || event.shiftKey) return false;
  return apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

/**
 * The chord written the way this platform's users read it: `⌘F` on Apple (their own convention —
 * glyph, no separator), `Ctrl+F` elsewhere (Windows convention, with the `+`).
 */
export function formatShortcut(shortcut: Shortcut, apple: boolean = isApplePlatform()): string {
  const key = shortcut.key.toUpperCase();
  return apple ? `⌘${key}` : `Ctrl+${key}`;
}
